import { Media } from "./Media";

export interface Task {
    _id: string;
    name: string;
    pretasks: any[];
    tasks: SubTask[];
    postasks: PosTask[];
    updated: Date;
    userId: string;
    active: boolean;
    authorship: string;
    aims: number[];
    aim_other: string;
    specific_aim: string;
    proficiency_level: number[];
    proficiency_level_comments: string;
    sessions: TaskSession[];
    clonned: boolean;
    original_source: string;
    original_task: string;
    weight: number;
    sectionId?: string;
    task_language: string;
}

export const emptyTask:Task = {
    _id: '',
    name: '',
    pretasks: [],
    tasks: [],
    postasks: [],
    updated: new Date(),
    userId: '',
    active: false,
    authorship: '',
    aims: [],
    aim_other: '',
    specific_aim: '',
    proficiency_level: [],
    proficiency_level_comments: '',
    sessions: [],
    clonned: false,
    original_source: '',
    original_task: '',
    weight: 0,
    sectionId: undefined,
    task_language: 'en',
}

// ── Nou model de pre/post-tasks (reestructuració) ──────────────────────────
// Veure taskgen-srv/docs/Disseny-PrePostTasks.md. Els valors de category/types
// són slugs alineats amb el backend (server/utils/prepostFormat.js).

export type MaterialKind = 'image' | 'video' | 'audio' | 'text';

export interface Material {
    kind: MaterialKind;
    description?: string | null;   // títol breu (una línia)
    file?: Media | null;           // per a image/video/audio
    text?: string;                 // per a kind 'text' — text pla (analitzador)
    formatted_text?: string;       // per a kind 'text' — HTML del rich text
    _key?: string;                 // només client: clau estable per a React/editor
}

// Interaction embedded (estructura tipus Main task). Reusa els sub-tipus de
// SubTask perquè el contingut és el mateix (grups/rols/prompts).
export interface Interaction {
    groups: SubTaskGroup[];
    roles: SubTaskRole[];
    prompts: SubTaskPrompt[];
}

// Tipus de pre-tasca / post-tasca (§3) — slugs.
export type PreTaskCategory = 'task_model' | 'warm_up' | 'developing' | 'vocabulary' | 'custom';
export type PosTaskCategory = 'task_model' | 'task_repetition' | 'explicit_explanation' | 'reflection' | 'written_oral' | 'custom';

// Tipus d'activitat (§4) — slugs.
export type ActivityType = 'listening' | 'watching' | 'reading' | 'speaking' | 'writing' | 'interaction' | 'exercise' | 'game';

// 'task_model' va al final (opció menys habitual al desplegable de creació).
export const PRETASK_CATEGORIES: PreTaskCategory[] = ['warm_up', 'developing', 'vocabulary', 'task_model'];
export const POSTASK_CATEGORIES: PosTaskCategory[] = ['task_repetition', 'explicit_explanation', 'reflection', 'written_oral', 'task_model'];
export const ACTIVITY_TYPES: ActivityType[] = ['listening', 'watching', 'reading', 'speaking', 'writing', 'interaction', 'exercise', 'game'];
// Task model només admet rebre informació (§3.1).
export const TASK_MODEL_ALLOWED_TYPES: ActivityType[] = ['listening', 'watching', 'reading'];
// Game descartat a v1 (§4): no apareix al selector però es manté al tipus.
export const ACTIVITY_TYPES_V1: ActivityType[] = ['listening', 'watching', 'reading', 'speaking', 'writing', 'interaction', 'exercise'];

export interface PreTask {
    _id: string;
    name: string;
    updated: Date;
    instructions: string;
    session: string;
    students: boolean;
    teachers: boolean;
    teacher_notes: string;
    // ── Format nou ──
    category: PreTaskCategory;
    types: ActivityType[];
    materials: Material[];
    interaction?: Interaction | null;   // singleton, si 'interaction' ∈ types
    exerciseId?: string | null;         // singleton, si 'exercise' ∈ types
    legacy_type?: string | null;
    // ── LEGACY (conservats en lectura durant la transició) ──
    type?: string;
    text?: string;
    formatted_text?: string;
    media?: Media[];
}

export interface PreTaskText {
    id: number;
    name: string;
    introduction: string;
    text: string;
    updated: Date;
}

export interface PreTaskAudio {
    id: number;
    name: string;
    introduction: string;
    text: string;
    media: Media[];
    updated: Date;
}

export interface SubTask {
    _id: string;
    name: string;
    updated: Date;
    groups: SubTaskGroup[];
    roles: SubTaskRole[];
    prompts: SubTaskPrompt[];
    goal: string;
    students: number;
    grouping: string;
    studentsPerGroup: number;
    session: string;
    students_session: boolean;
    teachers_session: boolean;
    teacher_notes: string;
}

export interface PosTask {
    _id: string;
    name: string;
    updated: Date;
    instructions: string;
    session: string;
    students: boolean;
    teachers: boolean;
    teacher_notes: string;
    // ── Format nou ──
    category: PosTaskCategory;
    types: ActivityType[];
    materials: Material[];
    interaction?: Interaction | null;   // singleton, si 'interaction' ∈ types
    exerciseId?: string | null;         // singleton, si 'exercise' ∈ types
    legacy_type?: string | null;
    // ── LEGACY (conservats en lectura durant la transició) ──
    type?: string;
    text?: string;
    formatted_text?: string;
    media?: Media[];
}

export interface SubTaskStudent {
    _id: string,
    name: string,
    role: number,
    prompt: number
}
export interface SubTaskGroup {
    _id: string;
    name: string;
    color: string;
    students: SubTaskStudent[];
}

export interface SubTaskRole {
    _id: string;
    name: string;
    color: string;
    groups: number[];
}

export interface SubTaskPrompt {
    _id: string;
    name: string;
    instructions: string;
    media: Media[];
    text: string;
    formatted_text: string;
    roles: number[];
    teacher_notes: string;
}

export interface TaskSession {
    _id: string,
    name: string,
    datetime: Date
}

export interface TaskExample {
    _id: string;
    task: {
        _id: string;
        name: string;
        updated: Date;
    },
    weight: number
}

export const emptyTaskExample = {
    _id: '',
    task: {
        _id: '',
        name: '',
        updated: new Date()
    },
    weight: 0
}

export interface TaskTag {
    _id: string;
    name: string;
    userId: string;
}

export interface TaskTagTask {
    _id: string;
    tagId: string;
    taskId: string;
}

export const aimsOfTask = [
    {
        value: 1,
        description: "To describe an object, person or place"
    },
    {
        value: 2,
        description: "To solve a communication problem or gap"
    },
    {
        value: 3,
        description: "To create a design or plan"
    },
    {
        value: 4,
        description: "To to make a decision or choice"
    },
    {
        value: 5,
        description: "To explain a process"
    },
    {
        value: 6,
        description: "To tell a story"
    },
    {
        value: 7,
        description: "To reach an agreement"
    },
    {
        value: 8,
        description: "To learn new content (focus on meaning)"
    },
    {
        value: 9,
        description: "To fill out a form"
    },
    {
        value: 10,
        description: "To make a complaint"
    },
    {
        value: 11,
        description: "To request information"
    },
    {
        value: 12,
        description: "To formulate an argument"
    },
    {
        value: 13,
        description: "To express an opinion"
    },
    {
        value: 14,
        description: "To foster critical language awareness"
    }
];

export const proficiencyLevelsOfTask = [
    {
        value: 1,
        description: "A1 Movers"
    },
    {
        value: 2,
        description: "A2 Key"
    },
    {
        value: 3,
        description: "B1 Preliminary"
    },
    {
        value: 4,
        description: "B2 First"
    },
    {
        value: 5,
        description: "C1 Advanced"
    },
    {
        value: 6,
        description: "C2 Proficiency"
    }
];

export interface TaskSection {
    _id: string;
    name: string;
    userId: string;
    parentId: string | null;
    weight: number;
    expanded: boolean;
    children?: TaskSection[];
}

export const emptyTaskSection: TaskSection = {
    _id: '',
    name: '',
    userId: '',
    parentId: null,
    weight: 0,
    expanded: true,
    children: [],
};

// Task sharing permission types
export type SharePermission = 'view' | 'edit';

export interface TaskShareUser {
    id: string;
    label: string;
    permission: SharePermission;
}

export interface SharedTaskInfo {
    _id: string;
    name: string;
    updated: Date;
    userId?: string;
    userFullName?: string;
    group?: string;
    permission?: SharePermission;
    canEdit?: boolean;
}
